import React, { useState, useEffect } from "react";
import { base44, reportsService } from "@/legacy/base44Client";
import { storageService, estatesService, forwardingService } from "@/services";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Upload, // Not used but kept for completeness based on original
  MapPin,
  Camera,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  Navigation
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { captureEvent } from "@/lib/analytics";

const categories = [
  { value: "pothole", label: "Pothole 🕳️", description: "Road damage or holes" },
  { value: "blocked_drain", label: "Blocked Drain 🚰", description: "Drainage issues" },
  { value: "waste_collection", label: "Waste Collection 🗑️", description: "Garbage or litter" },
  { value: "street_lighting", label: "Street Lighting 💡", description: "Broken lights" },
  { value: "flooding", label: "Flooding 🌊", description: "Water pooling" },
  { value: "vandalism", label: "Vandalism ⚠️", description: "Property damage" },
  { value: "illegal_dumping", label: "Illegal Dumping 🚮", description: "Unauthorized waste" },
  { value: "broken_infrastructure", label: "Broken Infrastructure 🔧", description: "General damage" },
  { value: "water_leak", label: "Water Leak 💧", description: "Pipe leaks" },
  { value: "other", label: "Other 📍", description: "Other issues" },
];

// Fix marker icon for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Map component for clicking to set location
function LocationPicker({ position, onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng);
    },
  });

  return position ? <Marker position={position} /> : null;
}

export default function ReportIssue() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    latitude: null,
    longitude: null,
    address: "",
    neighborhood: "",
    estate_id: null,
    unit_id: null,
  });

  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [detectedEstate, setDetectedEstate] = useState(null);
  const [estateUnits, setEstateUnits] = useState([]);

  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Check for estate when coordinates change
  useEffect(() => {
    if (formData.latitude && formData.longitude) {
      checkForEstate(formData.latitude, formData.longitude);
    }
  }, [formData.latitude, formData.longitude]);

  const checkForEstate = async (lat, lng) => {
    try {
      const estate = await estatesService.getEstateByCoordinates(lat, lng);
      if (estate) {
        setDetectedEstate(estate);
        setFormData(prev => ({ ...prev, estate_id: estate.id }));
        
        // Fetch units for this estate
        const units = await estatesService.getUnits(estate.id);
        setEstateUnits(units);
        
        if (units.length > 0) {
          toast.success(`📍 Estate detected: ${estate.name}. You can optionally select your unit.`, { duration: 3000 });
        }
      } else {
        setDetectedEstate(null);
        setEstateUnits([]);
        setFormData(prev => ({ ...prev, estate_id: null, unit_id: null }));
      }
    } catch (error) {
      console.error("Error checking for estate:", error);
    }
  };

  const getCurrentLocation = () => {
    setGettingLocation(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      setGettingLocation(false);
      toast.error("Geolocation not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lon,
        }));

        try {
          const { data, error } = await supabase.functions.invoke(
            `reverse-geocode?lat=${lat}&lon=${lon}`
          );

          if (error) throw error;

          setFormData(prev => ({
            ...prev,
            address: data.display_name,
            neighborhood: data.address?.suburb || data.address?.neighbourhood || data.address?.city_district || "",
          }));

          toast.success("📍 Location captured successfully!", { duration: 2000 });
        } catch (error) {
          console.error("Error geocoding:", error);
          toast.error("Couldn't get address details, but location is saved");
        }

        setGettingLocation(false);
      },
      (error) => {
        setLocationError("Unable to get your location. Please enable location services or enter manually.");
        setGettingLocation(false);
        toast.error("Please enable location services or enter address manually");
      }
    );
  };

  // Debounced address search with coordinate detection
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (formData.address && formData.address.length > 2) {
        // Check if input looks like coordinates (e.g., "-33.927, 18.422" or "-33.927,18.422")
        const coordPattern = /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/;
        if (coordPattern.test(formData.address.trim())) {
          const [lat, lng] = formData.address.split(',').map(s => parseFloat(s.trim()));
          if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
            // Valid coordinates - set them directly
            setFormData(prev => ({ 
              ...prev, 
              latitude: lat, 
              longitude: lng 
            }));
            // Reverse geocode to get address
            reverseGeocode(lat, lng);
            setAddressSuggestions([]);
            setShowSuggestions(false);
            return;
          }
        }
        // Regular address search
        searchAddress(formData.address);
      } else {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [formData.address]);

  const searchAddress = async (query) => {
    setSearchingAddress(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        `forward-geocode?q=${encodeURIComponent(query)}&limit=5`
      );

      if (error) throw error;

      setAddressSuggestions(data || []);
      setShowSuggestions(data && data.length > 0);
    } catch (error) {
      console.error("Error searching address:", error);
    }
    setSearchingAddress(false);
  };

  const selectSuggestion = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      address: suggestion.display_name,
      latitude: parseFloat(suggestion.lat),
      longitude: parseFloat(suggestion.lon),
      neighborhood: suggestion.address?.suburb || suggestion.address?.neighbourhood || suggestion.address?.city_district || "",
    }));
    setShowSuggestions(false);
    toast.success("📍 Location updated from suggestion", { duration: 2000 });
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const { data, error } = await supabase.functions.invoke(
        `reverse-geocode?lat=${lat}&lon=${lng}`
      );

      if (error) throw error;

      setFormData(prev => ({
        ...prev,
        address: data.display_name,
        neighborhood: data.address?.suburb || data.address?.neighbourhood || data.address?.city_district || "",
      }));

      toast.success("📍 Location set from coordinates!", { duration: 2000 });
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setFormData(prev => ({
        ...prev,
        address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      }));
      toast.success("📍 Coordinates captured", { duration: 2000 });
    }
  };

  const handleMapClick = async (latlng) => {
    setFormData(prev => ({
      ...prev,
      latitude: latlng.lat,
      longitude: latlng.lng,
    }));

    await reverseGeocode(latlng.lat, latlng.lng);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhotos(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const { file_url } = await storageService.uploadFile(file, 'report-photos');
        uploadedUrls.push(file_url);
      }
      setPhotos(prev => [...prev, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} photo${uploadedUrls.length > 1 ? 's' : ''} uploaded successfully!`, { duration: 2000 });
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo. Please try again.");
    }

    setUploadingPhotos(false);
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    toast.success("Photo removed", { duration: 1500 });
  };

  const createReportMutation = useMutation({
    mutationFn: async (reportData) => {
      return await reportsService.create(reportData);
    },
    onSuccess: async (newReport) => {
      captureEvent("community_issue_created", {
        report_id: newReport?.id,
        category: newReport?.category || formData.category,
        estate_id: newReport?.estate_id || formData.estate_id || null,
        unit_id: newReport?.unit_id || formData.unit_id || null,
        photos_count: photos.length,
      });

      queryClient.invalidateQueries({ queryKey: ['reports'] });
      
      // Automatically forward to COCT
      toast.success("🎉 Report submitted! Forwarding to COCT...", {
        duration: 3000,
      });

      try {
        const forwardResult = await forwardingService.forwardToCOCT(newReport.id);
        
        if (forwardResult.success) {
          toast.success("✅ Report forwarded to City of Cape Town successfully!", {
            duration: 4000,
          });
        } else {
          toast.warning("⚠️ Report saved but forwarding failed. We'll retry automatically.", {
            duration: 4000,
          });
          console.error('Forwarding error:', forwardResult.error);
        }
      } catch (error) {
        console.error('Error during forwarding:', error);
        toast.warning("⚠️ Report saved but couldn't forward. We'll retry later.", {
          duration: 4000,
        });
      }

      navigate(createPageUrl(`ReportDetails?id=${newReport.id}`));
    },
    onError: (error) => {
      captureEvent("community_issue_create_failed", {
        category: formData.category || null,
        estate_id: formData.estate_id || null,
        error: error?.message,
      });
      toast.error("Failed to submit report. Please check your connection and try again.");
      console.error("Error creating report:", error);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.category) {
      toast.error("Please select a category for your report");
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      toast.error("Location coordinates are required. Please use GPS or click on the map.");
      return;
    }

    if (!formData.title.trim()) {
      toast.error("Please provide a title for your report");
      return;
    }

    if (!formData.description.trim()) {
      toast.error("Please provide a description of the issue");
      return;
    }

    if (!formData.address.trim()) {
      toast.error("Please provide a location address");
      return;
    }

    createReportMutation.mutate({
      title: formData.title,
      description: formData.description,
      category: formData.category,
      latitude: formData.latitude,
      longitude: formData.longitude,
      address: formData.address,
      photo_urls: photos,
      status: "reported",
      created_by: user?.id,
      estate_id: formData.estate_id || null,
      unit_id: formData.unit_id || null,
    });
  };

  const isFormValid = formData.title && formData.description && formData.category && formData.latitude && formData.address;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-emerald-50 dark:from-slate-900 dark:to-slate-800 p-4 md:p-8 overflow-y-auto">
      <div className="max-w-3xl mx-auto pb-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 md:gap-4 mb-6"
        >
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(createPageUrl("MapView"))}
            className="border-teal-200 hover:bg-teal-50 dark:border-teal-800 dark:hover:bg-teal-900/20 flex-shrink-0 mt-1 rounded-xl"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
              Report an Issue
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Help improve your community
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="p-4 md:p-6 lg:p-8 bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl shadow-xl border-teal-100 dark:border-slate-700 rounded-2xl">
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              {/* Category Selection */}
              <div className="space-y-2">
                <Label htmlFor="category" className="text-slate-700 dark:text-slate-200 font-semibold">
                  Issue Category *
                </Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-700">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value} className="rounded-lg">
                        <div>
                          <div className="font-medium">{cat.label}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{cat.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title" className="text-slate-700 dark:text-slate-200 font-semibold">
                  Issue Title *
                </Label>
                <Input
                  id="title"
                  placeholder="Brief description (e.g., Large pothole on Main Road)"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="rounded-xl border-slate-200 dark:border-slate-700"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-slate-700 dark:text-slate-200 font-semibold">
                  Detailed Description *
                </Label>
                <Textarea
                  id="description"
                  placeholder="Provide more details about the issue..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  required
                  className="rounded-xl border-slate-200 dark:border-slate-700"
                />
              </div>

              {/* Location - Enhanced with autocomplete and map */}
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-200 font-semibold">
                  Location *
                </Label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                  GPS auto-fills, or type to search, or click map to pin exact location
                </p>
                <div className="flex flex-col sm:flex-row gap-2 relative">
                  <div className="flex-1 relative">
                    <Input
                      value={formData.address || (gettingLocation ? "Getting location..." : "")}
                      onChange={(e) => {
                        setFormData({ ...formData, address: e.target.value });
                      }}
                      onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder="Enter address, landmark, or area"
                      className="flex-1 min-w-0 rounded-xl border-slate-200 dark:border-slate-700"
                    />
                    {searchingAddress && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-teal-600" />
                    )}
                    
                    {/* Autocomplete suggestions */}
                    <AnimatePresence>
                      {showSuggestions && addressSuggestions.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg z-[1100] max-h-48 overflow-y-auto"
                        >
                          {addressSuggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => selectSuggestion(suggestion)}
                              className="w-full text-left px-4 py-3 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
                            >
                              <div className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                    {suggestion.display_name.split(',')[0]}
                                  </p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                                    {suggestion.display_name}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={getCurrentLocation}
                      disabled={gettingLocation}
                      className="border-teal-200 hover:bg-teal-50 dark:border-teal-800 dark:hover:bg-teal-900/20 rounded-xl flex-shrink-0"
                      title="Use Current Location"
                    >
                      {gettingLocation ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Navigation className="w-5 h-5" />
                      )}
                    </Button>
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setShowMap(!showMap)}
                      className="border-teal-200 hover:bg-teal-50 dark:border-teal-800 dark:hover:bg-teal-900/20 rounded-xl flex-shrink-0"
                      title="Pick on Map"
                    >
                      <MapPin className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
                
                {locationError && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{locationError}</span>
                  </p>
                )}
                
                {formData.latitude && formData.longitude && (
                  <p className="text-sm text-teal-600 dark:text-teal-400 flex items-center gap-1 mt-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">
                      GPS: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                      {formData.neighborhood && ` • ${formData.neighborhood}`}
                    </span>
                  </p>
                )}

                {/* Interactive Map */}
                <AnimatePresence>
                  {showMap && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 300 }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden rounded-xl border-2 border-teal-200 dark:border-teal-800 mt-3 relative z-0"
                    >
                      <div className="h-full w-full relative">
                        <div className="absolute top-2 left-2 z-[500] bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-md text-xs font-medium text-slate-700 dark:text-slate-200">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          Click to pin location
                        </div>
                        <MapContainer
                          center={formData.latitude && formData.longitude ? [formData.latitude, formData.longitude] : [-33.9249, 18.4241]} // Default to Cape Town if no location
                          zoom={13}
                          className="h-full w-full"
                          zoomControl={true}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <LocationPicker
                            position={formData.latitude && formData.longitude ? [formData.latitude, formData.longitude] : null}
                            onLocationSelect={handleMapClick}
                          />
                        </MapContainer>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Unit Selection (only shown if estate detected) */}
              <AnimatePresence>
                {detectedEstate && estateUnits.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2"
                  >
                    <Label htmlFor="unit" className="text-slate-700 dark:text-slate-200 font-semibold">
                      Unit Number (Optional)
                    </Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                      📍 Detected: {detectedEstate.name} - Select your unit if applicable
                    </p>
                    <Select 
                      value={formData.unit_id || "none"} 
                      onValueChange={(value) => setFormData({ ...formData, unit_id: value === "none" ? null : value })}
                    >
                      <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-700">
                        <SelectValue placeholder="Select your unit (optional)" />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl max-h-60">
                        <SelectItem value="none" className="rounded-lg">
                          <span className="text-slate-500">No unit / Common area</span>
                        </SelectItem>
                        {estateUnits.map((unit) => (
                          <SelectItem key={unit.id} value={unit.id} className="rounded-lg">
                            Unit {unit.unit_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Photo Upload */}
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-200 font-semibold">
                  Photos (Optional but recommended)
                </Label>
                <div className="border-2 border-dashed border-teal-200 dark:border-teal-800 rounded-2xl p-4 md:p-6 text-center hover:border-teal-400 dark:hover:border-teal-600 transition-all bg-teal-50/30 dark:bg-teal-950/20">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                    disabled={uploadingPhotos}
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2">
                      {uploadingPhotos ? (
                        <Loader2 className="w-6 h-6 md:w-8 md:h-8 text-teal-600 dark:text-teal-400 animate-spin" />
                      ) : (
                        <Camera className="w-6 h-6 md:w-8 md:h-8 text-teal-600 dark:text-teal-400" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white text-sm md:text-base">
                          {uploadingPhotos ? "Uploading..." : "Add Photos"}
                        </p>
                        <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">
                          Click to upload or take a photo
                        </p>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Photo Preview */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                    <AnimatePresence>
                      {photos.map((photo, index) => (
                        <motion.div
                          key={photo}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          className="relative group"
                        >
                          <img
                            src={photo}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-32 object-cover rounded-xl border-2 border-teal-200 dark:border-teal-800 shadow-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            aria-label="Remove photo"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(createPageUrl("MapView"))}
                  className="flex-1 order-2 sm:order-1 rounded-xl border-slate-200 dark:border-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isFormValid || createReportMutation.isPending}
                  className="flex-1 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 order-1 sm:order-2 rounded-xl shadow-lg shadow-teal-500/30 font-semibold"
                >
                  {createReportMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Submit Report
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </motion.div>

        {/* Help Text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900 rounded-2xl">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1 text-sm md:text-base">
                  Tips for Good Reports
                </h3>
                <ul className="text-xs md:text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                  <li>Use GPS or click the map to pinpoint the exact location</li>
                  <li>Type an address or landmark, and suggestions will appear</li>
                  <li>Include clear photos showing the issue from multiple angles</li>
                  <li>For issues "in the middle of the road," use the map to drop a pin</li>
                  <li>Provide detailed descriptions including size and safety concerns</li>
                </ul>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}